import type {
  CellRendererSelectorResult,
  ColDef,
  Column,
  GridApi,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
} from 'ag-grid-community';
import { createElement, useCallback, type ReactNode } from 'react';
import { isValidElementType } from 'react-is';
import type { BlotterTableAgGridContext } from './BlotterTableContext';

/**
 * Resolves the column id for the value shown in a group row, matching desktop
 * {@link getGroupedColumn} in `columns/group.tsx`.
 */
export function getGroupedColumnColId<TRow>(node: IRowNode<TRow>): string | null {
  const groupedColumn = node.group ? node.rowGroupColumn : node.parent?.rowGroupColumn;
  return groupedColumn?.getColId() ?? node.field ?? null;
}

/** Whether a column is shown in the grid (grouped columns are often hidden but still rendered in group rows). */
export function isAgGridColumnVisible(column: { isVisible?: () => boolean } | null | undefined): boolean {
  if (column?.isVisible == null) {
    return true;
  }
  return column.isVisible();
}

export interface RenderCellOptions {
  /**
   * When true, mounts the cell renderer even if the column is hidden in the grid.
   * Defaults to false so mobile cards respect the user's column visibility.
   */
  overrideColumnVisibility?: boolean;
}

function resolveColumnForRender<TRow>(api: GridApi<TRow>, node: IRowNode<TRow>, colId: string): Column<TRow> | null {
  const column = api.getColumn(colId);
  if (column) {
    return column;
  }

  if (node.group && node.rowGroupColumn?.getColId() === colId) {
    return node.rowGroupColumn;
  }

  return null;
}

function getValueWithGroupFallback<TRow>(
  api: GridApi<TRow>,
  node: IRowNode<TRow>,
  colId: string
): { column: Column<TRow> | null; value: unknown } {
  const column = resolveColumnForRender(api, node, colId);
  if (!column) {
    return { column: null, value: null };
  }

  const colDef = column.getColDef();
  const value = api.getCellValue({ rowNode: node, colKey: column });
  if (value != null) {
    return { column, value };
  }

  // Group/agg values are keyed inconsistently across blotter modes
  // (colId vs field vs custom id), so try every option.
  const keys = [column.getColId(), colId, colDef.field].filter((key): key is string => Boolean(key));
  const groupData = node.groupData as Record<string, unknown> | undefined;
  const aggregatedData = node.aggData as Record<string, unknown> | undefined;

  // Row-group mode: ag-grid stores per-column group values and aggregations on the node.
  for (const key of keys) {
    const groupedValue = groupData?.[key];
    if (groupedValue != null) {
      return { column, value: groupedValue };
    }

    const aggregatedValue = aggregatedData?.[key];
    if (aggregatedValue != null) {
      return { column, value: aggregatedValue };
    }
  }

  // Tree-data / auto-group column: the grouped column has no cell value,
  // only node.key (e.g. rollup name).
  // Match the column being rendered to that group level.
  if (node.group && node.key != null) {
    const groupedColId = getGroupedColumnColId(node);
    if (groupedColId != null && keys.includes(groupedColId)) {
      return { column, value: node.key };
    }
    if (node.field != null && keys.includes(node.field)) {
      return { column, value: node.key };
    }
  }

  return { column, value };
}

/** Shared column/cell state passed to formatter and renderer resolution helpers. */
interface FormatColDefValueContext<TRow> {
  api: GridApi<TRow>;
  node: IRowNode<TRow>;
  column: Column<TRow>;
  colDef: ColDef<TRow>;
  context: BlotterTableAgGridContext;
}

type RenderCellHookName = 'useRenderCell';

/**
 * Ensures ag-grid's `gridOptions.context` ref is present before building synthetic cell params.
 * BlotterTable always sets this ref; missing context indicates a misconfigured grid.
 */
function requireBlotterAgGridContext(
  context: BlotterTableAgGridContext | null | undefined,
  hookName: RenderCellHookName
): BlotterTableAgGridContext {
  if (context == null) {
    throw new Error(`${hookName} requires ag-grid context, but gridOptions.context was missing`);
  }
  return context;
}

/**
 * Fallback display when no cell renderer resolves: formatted string via valueFormatter,
 * otherwise the raw value as a string, or null for nullish values.
 */
function resolveFormattedCellValue<TRow>(value: unknown, formatContext: FormatColDefValueContext<TRow>): ReactNode {
  const { colDef } = formatContext;
  if (typeof colDef.valueFormatter === 'function') {
    return formatColDefValue(value, formatContext) ?? null;
  }
  return value == null ? null : value.toString();
}

/**
 * Resolves and mounts the column's cell renderer, mirroring ag-grid's lookup order:
 * selector component → colDef.cellRenderer → components map or direct component reference.
 * Returns null when no renderer is found so the caller can fall back to formatted text.
 */
function resolveCellRendererElement<TRow>({
  value,
  formatContext,
  components,
}: {
  value: unknown;
  formatContext: FormatColDefValueContext<TRow>;
  components: Record<string, any>;
}): ReactNode | null {
  const cellParams = buildSyntheticCellRendererParams(value, formatContext);
  const selectorResult = resolveCellRendererSelector(formatContext.colDef, cellParams);
  const resolvedParams = resolveCellRendererParams(formatContext.colDef, cellParams, selectorResult);
  const rendererKey = selectorResult?.component ?? formatContext.colDef.cellRenderer;

  // String key → look up in the components map (e.g. 'sizeColumn', 'priceColumn').
  if (typeof rendererKey === 'string') {
    const Renderer = components[rendererKey];
    if (Renderer) {
      return createElement(Renderer, resolvedParams);
    }
  }

  // Direct component reference (supports memo/forwardRef wrappers).
  if (rendererKey != null && typeof rendererKey !== 'string' && isValidElementType(rendererKey)) {
    return createElement(rendererKey as any, resolvedParams);
  }

  return null;
}

/**
 * Invokes the column's valueFormatter with a synthetic ValueFormatterParams object.
 * Returns undefined when no formatter is defined, null when the formatter returns nullish.
 */
function formatColDefValue<TRow>(
  rawValue: unknown,
  { colDef, node, column, api, context }: FormatColDefValueContext<TRow>
): string | null | undefined {
  if (typeof colDef.valueFormatter !== 'function') {
    return undefined;
  }
  const valueFormatterParams: ValueFormatterParams<TRow> = {
    value: rawValue,
    data: node.data,
    node,
    colDef,
    column,
    api,
    context,
  };
  const formatted = colDef.valueFormatter(valueFormatterParams);
  return formatted == null ? null : String(formatted);
}

/**
 * Builds a synthetic ICellRendererParams object for mounting a blotter cell renderer
 * outside ag-grid's cell DOM. Includes compatibility stubs for params some renderers expect.
 */
function buildSyntheticCellRendererParams<TRow>(
  value: unknown,
  formatContext: FormatColDefValueContext<TRow>
): ICellRendererParams<TRow> {
  const { api, node, column, colDef, context } = formatContext;
  const eGridCell = document.createElement('div');
  const eParentOfValue = document.createElement('div');
  const getFormattedValue = (rawValue: unknown) => formatColDefValue(rawValue, formatContext);

  return {
    value,
    data: node.data,
    node,
    api,
    column,
    colDef,
    context,
    getValue: () => value,
    setValue: () => false,
    // Match ag-grid behavior by delegating to colDef.valueFormatter when present.
    formatValue: (v: unknown) => {
      const rawValue = v === undefined ? value : v;
      const formatted = getFormattedValue(rawValue);
      return formatted ?? (rawValue == null ? '' : String(rawValue));
    },
    refreshCell: () => {},
    // Compatibility-only: provide detached placeholder elements because this
    // render path is outside ag-grid's real cell DOM tree.
    eGridCell,
    eParentOfValue,
    registerRowDragger: () => {},
    fullWidth: true,
    valueFormatted: getFormattedValue(value),
    // Compatibility-only: some renderers call this ag-grid helper.
    // We don't manage native tooltip state in this synthetic render path.
    setTooltip: () => {},
  };
}

/**
 * Merges colDef.cellRendererParams (static or function) with synthetic cell params,
 * then overlays any params returned by cellRendererSelector.
 */
function resolveCellRendererParams<TRow>(
  colDef: ColDef<TRow>,
  cellParams: ICellRendererParams<TRow>,
  selectorResult: CellRendererSelectorResult | undefined
): ICellRendererParams<TRow> {
  const resolvedCellRendererParams =
    typeof colDef.cellRendererParams === 'function'
      ? { ...cellParams, ...colDef.cellRendererParams(cellParams) }
      : { ...cellParams, ...colDef.cellRendererParams };

  if (selectorResult?.params == null) {
    return resolvedCellRendererParams;
  }
  return { ...resolvedCellRendererParams, ...selectorResult.params };
}

/** Runs colDef.cellRendererSelector when defined, otherwise returns undefined. */
function resolveCellRendererSelector<TRow>(
  colDef: ColDef<TRow>,
  cellParams: ICellRendererParams<TRow>
): CellRendererSelectorResult | undefined {
  if (typeof colDef.cellRendererSelector !== 'function') {
    return undefined;
  }
  return colDef.cellRendererSelector(cellParams) as CellRendererSelectorResult | undefined;
}

/**
 * Renders the cell for a given column ID by mounting the existing ag-grid renderer
 * with a synthetic ICellRendererParams object. Used by mobile cards and detail
 * sheets to reuse the same cell renderers (AgGridSize, AgGridPrice, etc.) that
 * the desktop blotter uses, without re-implementing column-specific formatting.
 */
export function useRenderCell<TRow>(
  api: GridApi<TRow> | undefined,
  node: IRowNode<TRow> | undefined,
  components: Record<string, any>
): (colId: string, options?: RenderCellOptions) => ReactNode {
  return useCallback(
    (colId: string, options?: RenderCellOptions): ReactNode => {
      if (!api || !node) {
        return null;
      }
      const { column, value } = getValueWithGroupFallback(api, node, colId);
      if (!column) {
        return null;
      }

      const overrideColumnVisibility = options?.overrideColumnVisibility ?? false;
      if (!overrideColumnVisibility && !isAgGridColumnVisible(column)) {
        return null;
      }

      const colDef = column.getColDef();
      const context = requireBlotterAgGridContext(api.getGridOption('context'), 'useRenderCell');
      const formatContext: FormatColDefValueContext<TRow> = {
        api,
        node,
        column,
        colDef,
        context,
      };

      // Prefer mounting the column's cell renderer when one resolves.
      const rendered = resolveCellRendererElement({ value, formatContext, components });
      if (rendered != null) {
        return rendered;
      }

      // No renderer — fall back to valueFormatter output or raw value.toString().
      return resolveFormattedCellValue(value, formatContext);
    },
    [api, node, components]
  );
}
